export const SPIN_DURATION_MS = 10_000;
export const SPIN_STAGE_MS = 2_000;

const FULL_TURN = 360;
const DECELERATION_POWER = 2.2;
const EPSILON = 1e-7;

export const SPIN_STAGES = Object.freeze([
  "fast",
  "decelerating",
  "slow",
  "very-slow",
  "super-slow",
]);

export function normalizeAngle(angle) {
  return ((Number(angle) % FULL_TURN) + FULL_TURN) % FULL_TURN;
}

export function pinAngles(segmentCount) {
  const count = Math.max(1, Number(segmentCount) || 1);
  return Array.from({ length: count }, (_, index) => (index * FULL_TURN) / count);
}

export function spinStage(elapsedMs) {
  return Math.min(SPIN_STAGES.length - 1, Math.max(0, Math.floor(elapsedMs / SPIN_STAGE_MS)));
}

export function weightedSpinProgress(elapsedMs, durationMs = SPIN_DURATION_MS) {
  const t = Math.min(1, Math.max(0, elapsedMs / Math.max(1, durationMs)));
  return 1 - Math.pow(1 - t, DECELERATION_POWER);
}

export function safeWinningAngle(result, fallbackCount) {
  const count = Math.max(1, Number(result?.segment_count) || Number(fallbackCount) || 1);
  const index = Number(result?.index);
  if (Number.isInteger(index) && index >= 0 && index < count) {
    return normalizeAngle(FULL_TURN - ((index + 0.5) * FULL_TURN) / count);
  }
  return normalizeAngle(Number(result?.final_angle) || 0);
}

export function createSpinPlan(currentAngle, finalAngle, segmentCount) {
  const count = Math.max(1, Number(segmentCount) || 1);
  const startAngle = Number(currentAngle) || 0;
  const targetAngle = normalizeAngle(finalAngle);
  const deltaToTarget = normalizeAngle(targetAngle - normalizeAngle(startAngle));
  // More complete turns for wheels with fewer segments keeps the final 2 seconds
  // slow while still allowing the player to count real pin contacts.
  const completeTurns = Math.max(8, Math.ceil(44 / count));
  const endAngle = startAngle + completeTurns * FULL_TURN + deltaToTarget;
  return { startAngle, endAngle, targetAngle, completeTurns };
}

export function crossedPins(fromAngle, toAngle, segmentCount) {
  const count = Math.max(1, Number(segmentCount) || 1);
  const step = FULL_TURN / count;
  if (!(toAngle > fromAngle)) return [];

  const firstBoundary = Math.floor((fromAngle + EPSILON) / step) + 1;
  const lastBoundary = Math.floor((toAngle + EPSILON) / step);
  const distance = toAngle - fromAngle;
  const crossings = [];

  for (let boundaryIndex = firstBoundary; boundaryIndex <= lastBoundary; boundaryIndex += 1) {
    const angle = boundaryIndex * step;
    crossings.push({
      boundaryIndex,
      angle,
      frameFraction: Math.min(1, Math.max(0, (angle - fromAngle) / distance)),
    });
  }
  return crossings;
}
